// SCR-016: review and approve or reject one proposed change. Only the server-rendered summary,
// risk flags and digest are shown here — no agent prose. Approve is two-step (tick the review box,
// then Approve) and always sends back the digest exactly as displayed (`approveChangeRequest`);
// the controls are absent for a principal without `installer:approve` (REQ-046).
import { useContext, useEffect, useState } from "preact/hooks";
import type { JSX } from "preact";
import type { ScreenProps } from "../../router/route";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext } from "../../session/session";
import { ErrorBanner, Loading } from "@nexus/ui";
import { ParameterDiff, ResourceDiff } from "../../ui/diff";
import { Checkbox, Field, TitleWithHelp, errorId } from "@nexus/ui";
import { Card, Chip, CopyId, PageHeader } from "@nexus/ui";
import { Link, useBreadcrumb } from "../../ui/shell";
import { actorDisplay, actorTitle, knownActor } from "../../ui/identity";
import { InstallerDisabled } from "./disabled";
import {
  approvalErrorMessage,
  approveChangeRequest,
  changeApprovalView,
  changeKindLabel,
  changeStatusTone,
  installerSessionPath,
  isInstallerDisabled,
  rejectChangeRequest,
  rejectReasonRule,
  sanitizedParameters,
  sanitizedResources,
  type InstallerChange,
} from "./model";

type Params = { sessionId: string; changeId: string };

export function ChangeApproval({ params }: ScreenProps<"/installer/sessions/:sessionId/changes/:changeId">) {
  const { sessionId, changeId } = params as Params;
  const api = useApi();
  const services = useContext(SessionContext);
  const canApprove = services?.state.status === "signed-in" && services.state.session.can("installer:approve");

  const [change, setChange] = useState<InstallerChange | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [now, setNow] = useState(() => new Date());
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ApiError | null>(null);
  const [rejectReason, setRejectReason] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useBreadcrumb([
    { label: "Session", href: installerSessionPath(sessionId) },
    { label: change ? changeKindLabel(change.kind) : "Change" },
  ]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    setLoadError(null);
    api
      .get("/v1/installer/sessions/{sessionId}/changes/{changeId}", { path: { sessionId, changeId } })
      .then((loaded) => {
        if (!cancelled) setChange(loaded);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (error instanceof ApiError) setLoadError(error);
        else throw error;
      });
    return () => {
      cancelled = true;
    };
  }, [api, sessionId, changeId, reloadToken]);

  function reload() {
    setChange(null);
    setReviewed(false);
    setReloadToken((n) => n + 1);
  }

  async function approve() {
    if (!api || !change) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.post("/v1/installer/sessions/{sessionId}/changes/{changeId}/approve", {
        path: { sessionId, changeId },
        body: approveChangeRequest(change),
      });
      setChange(updated);
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setActionError(e);
      if (e.code === "artifact_changed") reload();
    } finally {
      setBusy(false);
    }
  }

  async function reject(event: JSX.TargetedEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!api || rejectReason === null) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await api.post("/v1/installer/sessions/{sessionId}/changes/{changeId}/reject", {
        path: { sessionId, changeId },
        body: rejectChangeRequest(rejectReason),
      });
      setChange(updated);
      setRejectReason(null);
    } catch (e) {
      if (!(e instanceof ApiError)) throw e;
      setActionError(e);
    } finally {
      setBusy(false);
    }
  }

  if (!api) return <p role="alert">Admin API not available.</p>;
  if (isInstallerDisabled(loadError)) return <InstallerDisabled />;
  if (loadError)
    return (
      <div class="page change-approval">
        <PageHeader title="Change approval" />
        <ErrorBanner error={loadError} onRetry={reload} />
      </div>
    );
  if (!change) return <Loading label="Loading the change…" />;

  const view = changeApprovalView(change, now, !!canApprove, reviewed);
  const rejectProblem = rejectReason === null ? null : rejectReasonRule(rejectReason);
  const me = services?.state.status === "signed-in" ? services.state.session.me : null;

  return (
    <div class="page change-approval">
      <PageHeader
        title={view.kindLabel}
        titleExtra={<Chip tone={changeStatusTone(change.status)}>{view.statusLabel}</Chip>}
        lede="Review exactly what will be written to the customer's account. Approving binds to the digest below; nothing else can be applied."
        meta={
          <>
            <span>{view.expiresInText}</span>
            <CopyId id={changeId} label="Change" what="change id" />
            <Link href={installerSessionPath(sessionId)}>Back to the session</Link>
          </>
        }
      />
      {actionError && (
        <p role="alert" class="banner banner-error">
          {approvalErrorMessage(actionError)}
        </p>
      )}
      {view.riskFlags.length > 0 && (
        <section class="card risk-flags" aria-labelledby="risk-flags-heading">
          <TitleWithHelp id="risk-flags-heading" title="Risk flags" help="installer.riskFlags" />
          <ul class="risk-flag-list">
            {view.riskFlags.map((flag) => (
              <li key={flag}>
                <Chip tone="amber">{flag}</Chip>
              </li>
            ))}
          </ul>
        </section>
      )}
      <Card title="What changes" titleId="diff-heading" class="change-diff">
        <ResourceDiff resources={sanitizedResources(change.summary.resources)} />
        <ParameterDiff parameters={sanitizedParameters(change.summary.parameters)} />
      </Card>
      <section class="card artifact-digest" aria-labelledby="digest-heading">
        <TitleWithHelp id="digest-heading" title="Artifact digest" help="installer.digest" />
        <code class="secret-value">{view.digest}</code>
      </section>
      {!canApprove ? (
        <p class="banner banner-info" role="note">
          You can review this change, but approving or rejecting it needs the installer approver permission
          (<code>installer:approve</code>). Ask a super-admin if you need it.
        </p>
      ) : (
        change.status === "proposed" && (
          <section class="card change-actions" aria-labelledby="decide-heading">
            <h2 id="decide-heading">Approve or reject</h2>
            <div class="split-even">
              <div class="stack-sm">
                <Checkbox
                  id="change-reviewed"
                  label="I reviewed the diff above"
                  help="installer.reviewed"
                  checked={reviewed}
                  onChange={setReviewed}
                />
                <div class="row">
                  <button type="button" class="button button-primary" disabled={!view.canApprove || busy} onClick={() => void approve()}>
                    {busy ? "Approving…" : "Approve"}
                  </button>
                </div>
              </div>
              <form class="reject-form stack-sm" onSubmit={(event) => void reject(event)}>
                <Field
                  id="reject-reason"
                  label="Reason for rejecting"
                  help="installer.rejectReason"
                  error={rejectReason !== null ? rejectProblem : null}
                >
                  <textarea
                    id="reject-reason"
                    maxLength={500}
                    value={rejectReason ?? ""}
                    aria-invalid={rejectReason !== null && rejectProblem ? "true" : undefined}
                    aria-describedby={rejectReason !== null && rejectProblem ? errorId("reject-reason") : undefined}
                    onInput={(event) => setRejectReason(event.currentTarget.value)}
                  />
                </Field>
                <div class="row">
                  <button type="submit" class="button button-danger" disabled={busy || rejectReason === null || rejectProblem !== null}>
                    Reject
                  </button>
                </div>
              </form>
            </div>
          </section>
        )
      )}
      {change.status !== "proposed" && (
        <section class="card change-history" aria-label="History">
          {change.approvedBy && (
            <p>
              Approved by <span title={actorTitle(change.approvedBy, knownActor(change, "approvedBy"))}>{actorDisplay(change.approvedBy, me, knownActor(change, "approvedBy"))}</span>
              {change.approvedAt && <> · {when(change.approvedAt)}</>}
            </p>
          )}
          {change.rejectedBy && (
            <p>
              Rejected by <span title={actorTitle(change.rejectedBy, knownActor(change, "rejectedBy"))}>{actorDisplay(change.rejectedBy, me, knownActor(change, "rejectedBy"))}</span>: {change.rejectedReason}
            </p>
          )}
          {change.appliedAt && <p>Applied {when(change.appliedAt)}</p>}
          {change.errorCode && (
            <p role="alert" class="banner banner-error">
              Failed: <code>{change.errorCode}</code>
            </p>
          )}
        </section>
      )}
    </div>
  );
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
